import React, {useEffect, useMemo, useCallback, useState} from 'react'

import {useDisclosure} from '@chakra-ui/react'
import {log} from '@/logger'

import {useRequiredAuth} from '../../../stores/AuthStore'

import {Photo, Album, FeatureName} from '../../../types'
import {useReleaseFlags} from '../../../hooks/useReleaseFlags'
import {useIsCampMinderEmployee} from '../../../hooks/useIsCampMinderEmployee'
import {useScrollToPhotoParam} from '../../../hooks/useScrollToPhotoParam'
import {useConfirmation} from '../../../app/ConfirmationManager'
import {useStatusToast} from '../../../app/ToastManager'
import {deleteAlbumPhoto} from '../../../services/api/photos/deleteAlbumPhoto'
import {useAlbumService} from '../../../services/useAlbumService'
import {usePhotoRotation} from '../../../services/usePhotoRotation'

import copy from '../copy.json'
import {usePreventTabClose} from '../../../hooks/usePreventTabClose'

import {GalleryPhoto} from '../types'
import {useAlbumDispatch} from '../AlbumManager'
import {
  useAlbumPhotosDispatch,
  useAlbumPhotosState,
} from '../AlbumPhotosManager'
import {useSSE} from '../../../app/SSEProvider'
import {useFetchAlbumUploadSessions} from '../../../services/useFetchAlbumUploadSessions'

import {
  TopicUploadSessionComplete,
  processUploadSessionCompleteSSE,
} from '../../../services/api/photos/processUploadSessionCompleteSSE'
import {
  PhotoGallery,
  FaceConfidenceModal,
  PhotoTagModal,
} from '../../../components'
import fallbackImage from '../../../assets/images/placeholderPhoto.jpg'

// CampMinder-employee-only action label for the per-photo snowman menu item.
// Gating is now driven declaratively by the action's `employeeOnly` flag (see
// the actions list below), not by matching against this display string.
const FACE_CONFIDENCE_ACTION_NAME = 'See Facial Recognition Confidence'
const TAGGED_CAMPERS_ACTION_NAME = 'Tagged Campers'

type PhotoType = 'standard' | 'thumbnail'

const getPhotoDimension = (
  photo: Photo,
  photoType: PhotoType,
  dimension: 'Height' | 'Width',
): number =>
  photo[`${photoType}${dimension}`] > 0
    ? photo[`${photoType}${dimension}`]
    : 150

interface Props {
  isVisible: boolean
  album: Album
}

export const DefaultGallery: React.FC<React.PropsWithChildren<Props>> =
  React.memo(({isVisible, album}) => {
    const albumDispatch = useAlbumDispatch()
    const {
      data: photos,
      // processSSE,
      status,
      // numUnprocessed,
    } = useAlbumPhotosState()
    const albumPhotosDispatch = useAlbumPhotosDispatch()

    const {client} = useRequiredAuth()
    const {getIsReleasedToUser} = useReleaseFlags()
    // Gates every `employeeOnly` action, not just FR confidence — the
    // per-action server-side backstop differs (see the `actions.filter` below).
    const isCampMinderEmployee = useIsCampMinderEmployee()
    const showConfirmation = useConfirmation()

    // Facial-recognition-confidence modal (CampMinder-employee-only).
    const {
      isOpen: isFaceConfidenceOpen,
      onOpen: onFaceConfidenceOpen,
      onClose: onFaceConfidenceClose,
    } = useDisclosure()
    const [selectedFacePhoto, setSelectedFacePhoto] = useState<{
      photoId: number
      clientId: number
    } | null>(null)

    // Tagged Campers modal (CampMinder-employee-only, read-only).
    const {
      isOpen: isTaggedCampersOpen,
      onOpen: onTaggedCampersOpen,
      onClose: onTaggedCampersClose,
    } = useDisclosure()
    const [selectedTagPhoto, setSelectedTagPhoto] = useState<{
      photoId: number
      clientId: number
      photoUrl: string
    } | null>(null)

    const {toast} = useStatusToast()
    const {upsert} = useAlbumService()

    const {
      numUnprocessedFiles,
      failedProcessingFiles,
      fetchData: fetchAlbumUploadSessions,
      status: albumSessionStatus,
    } = useFetchAlbumUploadSessions(album?.ID)

    const {
      displayPhotoRotations,
      addRotation,
      rotatePhotoStatus,
      processingPhotos,
      clearAllPhotoRotations,
    } = usePhotoRotation()

    // prevent close or refresh if we have processing photos
    const preventTabClose = !!Object.keys(processingPhotos).length
    usePreventTabClose(preventTabClose)

    // SSE Subscriptions
    const subscribe = useSSE()
    // // Process Photo SSE
    // useEffect(() => {
    //   if (!subscribe) {
    //     return (): void => {
    //       // don't unsubscribe before you can subscribe
    //     }
    //   }

    //   const unsubscribe = subscribe(TopicProcessPhotoComplete, processSSE)

    //   return unsubscribe
    // }, [client, subscribe, processSSE])

    // Upload Session Complete SSE
    useEffect(() => {
      if (!subscribe) {
        return (): void => {
          // don't unsubscribe before you can subscribe
        }
      }

      const unsubscribe = subscribe(TopicUploadSessionComplete, (msg) => {
        if (status === 'uploading') return

        const sse = processUploadSessionCompleteSSE(msg)

        // When we get an Upload Complete SSE for this album, refresh the data
        if (sse.albumID === album.ID) {
          albumDispatch({type: 'RESET'})
          albumPhotosDispatch({type: 'RESET'})
        }
      })

      return unsubscribe
    }, [album, subscribe, albumDispatch, albumPhotosDispatch, status])

    // Rotate Errors
    useEffect(() => {
      if (rotatePhotoStatus !== 'ok') {
        toast.error(copy.rotatePhoto.failure)
      }
    }, [toast, rotatePhotoStatus])

    // Clear rotations if after successfully fetching photos
    useEffect(() => {
      if (status === 'resolved') {
        clearAllPhotoRotations()
      }
    }, [status, clearAllPhotoRotations])

    useEffect(() => {
      if (
        Object.keys(displayPhotoRotations).length &&
        !Object.keys(processingPhotos).length
      ) {
        albumPhotosDispatch({type: 'RESET'})
      }
    }, [processingPhotos, displayPhotoRotations, albumPhotosDispatch])

    // Fetch Album Upload Session data after the photo fetching completes successfully
    useEffect(() => {
      const fetchAlbumData = async (): Promise<void> => {
        if (status === 'resolved') {
          await fetchAlbumUploadSessions()
        }
      }

      fetchAlbumData()
    }, [status, fetchAlbumUploadSessions])

    // Determine when we are currently processing photos
    useEffect(() => {
      if (albumSessionStatus === 'resolved' && numUnprocessedFiles > 0) {
        albumPhotosDispatch({
          type: 'PROCESSING_PHOTOS',
          payload: numUnprocessedFiles,
        })
      }
    }, [numUnprocessedFiles, albumPhotosDispatch, albumSessionStatus])

    // Show failed file UI when there are failures
    useEffect(() => {
      if (
        albumSessionStatus === 'resolved' &&
        failedProcessingFiles.length > 0
      ) {
        albumPhotosDispatch({
          type: 'SET_FAILED_FILES',
          payload: failedProcessingFiles,
        })
      }
    }, [failedProcessingFiles, albumPhotosDispatch, albumSessionStatus])

    // After all photos are processed, reset everything to get fresh album/albumphotos data pulls
    // useEffect(() => {
    // TODO: i've seen negative numUnprocessed. Not sure if I should change condition or find out why that is happening
    // if (numUnprocessed === 0 && status === 'processing') {
    //   albumDispatch({type: 'RESET'})
    //   albumPhotosDispatch({type: 'RESET'})
    // }
    // }, [numUnprocessed, albumDispatch, albumPhotosDispatch, status])

    const transformPhotos = React.useCallback(
      (photoList: Photo[], photoType: PhotoType): GalleryPhoto[] => {
        const transformedPhotos = photoList.map((p) => {
          const transformed = {
            key: p.ID,
            src:
              p[`${photoType}Height`] > 0 && p[`${photoType}Width`] > 0
                ? p[`${photoType}URL`]
                : fallbackImage,
            width: [90, 270].includes(displayPhotoRotations[p.ID])
              ? getPhotoDimension(p, photoType, 'Height')
              : getPhotoDimension(p, photoType, 'Width'),
            height: [90, 270].includes(displayPhotoRotations[p.ID])
              ? getPhotoDimension(p, photoType, 'Width')
              : getPhotoDimension(p, photoType, 'Height'),
            rotation: displayPhotoRotations[p.ID]
              ? displayPhotoRotations[p.ID]
              : 0,
            processing: processingPhotos[p.ID],
            originalFileName: p.originalFileName,
          }
          return transformed
        })
        return transformedPhotos
      },
      [displayPhotoRotations, processingPhotos],
    )

    const [transformedThumbnailPhotos] = React.useMemo(
      () => [transformPhotos(photos, 'thumbnail')],
      [photos, transformPhotos],
    )

    const onDelete = useCallback(
      (itemID: Photo['ID']) => (): void => {
        showConfirmation({
          variant: 'danger',
          title: 'Delete Photo',
          description: "Are you sure? You can't undo this action afterwards.",
          confirmButtonText: 'Delete',
          testID: 'delete-photo',
          onConfirm: async (): Promise<void> => {
            const deletion = await deleteAlbumPhoto(client.ID, itemID)

            if (deletion.result === 'success') {
              albumPhotosDispatch({type: 'REMOVE_PHOTO', payload: itemID})
              albumDispatch({type: 'REMOVE_PHOTO'})
              toast.success('Photo successfully deleted.')

              log.breadcrumb('Album photo successfully deleted', {
                photoID: itemID,
              })
            } else {
              const GENERIC_DELETE_ERROR =
                'Something went wrong deleting your photo. Please try again.'

              toast.error(deletion.error.message || GENERIC_DELETE_ERROR)
              log.breadcrumb('Album photo unsuccessfully deleted', {
                photoID: itemID,
              })
            }
          },
        })
      },
      [albumDispatch, albumPhotosDispatch, client.ID, showConfirmation, toast],
    )

    const onCoverPhotoSelection = useCallback(
      (itemID: Album['coverPhotoID']) => async (): Promise<void> => {
        const albumToUpdate = {...album, coverPhotoID: itemID}
        const response = await upsert(albumToUpdate)

        if (response.status === 'success') {
          albumDispatch({type: 'SET_COVER_PHOTO', payload: itemID})

          toast.success('Cover photo successfully updated.')
          log.breadcrumb('Album cover photo successfully set', {
            photoID: itemID,
          })
        } else {
          const GENERIC_UPDATE_ERROR =
            'Something went wrong, your cover photo did not update. Please try again.'
          toast.error(response.message || GENERIC_UPDATE_ERROR)
          log.breadcrumb('Album cover photo unsuccessfully set', {
            photoID: itemID,
          })
        }
      },
      [album, albumDispatch, toast, upsert],
    )

    const onRotate = useCallback(
      (itemID: Photo['ID']) => (): void => {
        const photo = transformedThumbnailPhotos.find(
          (image) => image.key === itemID,
        )

        if (!photo || !photo.key) return

        addRotation(photo.key)
      },
      [addRotation, transformedThumbnailPhotos],
    )

    const onViewFaceConfidence = useCallback(
      (itemID: Photo['ID']) => (): void => {
        const photo = photos.find((p) => p.ID === itemID)

        if (!photo) return

        const photoId = parseInt(photo.ID, 10)
        const clientId = parseInt(client.ID, 10)

        // Guard against non-numeric ids: the FR-confidence endpoint keys on
        // numeric PhotoID/ClientID, so a NaN would build a nonsensical request.
        // No-op (with a breadcrumb) rather than opening a modal that can only
        // fail.
        if (Number.isNaN(photoId) || Number.isNaN(clientId)) {
          log.breadcrumb(
            'Skipped FR-confidence modal: non-numeric photo/client id',
            {photoID: photo.ID, clientID: client.ID},
          )
          return
        }

        setSelectedFacePhoto({photoId, clientId})
        onFaceConfidenceOpen()
      },
      [photos, client.ID, onFaceConfidenceOpen],
    )

    // Reset the selected photo on close so the modal fully unmounts and its
    // internal state (e.g. the "show all attempts" toggle) never leaks across
    // different photos. Paired with the `key` below for defense in depth.
    const onFaceConfidenceModalClose = useCallback((): void => {
      onFaceConfidenceClose()
      setSelectedFacePhoto(null)
    }, [onFaceConfidenceClose])

    const onViewTaggedCampers = useCallback(
      (itemID: Photo['ID']) => (): void => {
        const photo = photos.find((p) => p.ID === itemID)

        if (!photo) return

        const photoId = parseInt(photo.ID, 10)
        const clientId = parseInt(client.ID, 10)

        // Guard against non-numeric ids, same rationale as onViewFaceConfidence:
        // a NaN would build a nonsensical request, so no-op with a breadcrumb
        // rather than opening a modal that can only fail.
        if (Number.isNaN(photoId) || Number.isNaN(clientId)) {
          log.breadcrumb(
            'Skipped Tagged Campers modal: non-numeric photo/client id',
            {photoID: photo.ID, clientID: client.ID},
          )
          return
        }

        setSelectedTagPhoto({photoId, clientId, photoUrl: photo.standardURL})
        onTaggedCampersOpen()
      },
      [photos, client.ID, onTaggedCampersOpen],
    )

    // Reset the selected photo on close, same rationale as
    // onFaceConfidenceModalClose: the modal fully unmounts so no per-photo
    // state leaks into the next photo.
    const onTaggedCampersModalClose = useCallback((): void => {
      onTaggedCampersClose()
      setSelectedTagPhoto(null)
    }, [onTaggedCampersClose])

    const actions = useMemo<
      {
        name: string
        onClick: (itemID: Photo['ID']) => () => void
        feature: FeatureName
        // When true, the action is only shown to CampMinder employees (gated
        // client-side by `isCampMinderEmployee`). Declarative alternative to
        // matching against a display-string label.
        employeeOnly?: boolean
      }[]
    >(
      () => [
        {
          name: 'Make Album Cover',
          onClick: onCoverPhotoSelection,
          feature: 'photo-action',
        },
        {
          name: 'Rotate 90° Clockwise',
          onClick: onRotate,
          feature: 'photo-action',
        },
        {
          name: 'Delete',
          onClick: onDelete,
          feature: 'photo-action',
        },
        {
          name: FACE_CONFIDENCE_ACTION_NAME,
          onClick: onViewFaceConfidence,
          feature: 'photo-action',
          employeeOnly: true,
        },
        {
          name: TAGGED_CAMPERS_ACTION_NAME,
          onClick: onViewTaggedCampers,
          feature: 'photo-action',
          // PRIVACY: deleting this line is not a UI-scope change. The modal
          // renders "tagged by <employee name>" on manual rows, so widening this
          // action to camp Users (the direction of travel per the AB#23610 plan,
          // D2) also discloses WHICH CampMinder employee tagged each camper.
          // Decide that exposure deliberately — drop the attribution line in
          // PhotoTagModal's TaggedRow, or confirm it is intended — rather than
          // inheriting it as a side effect of removing the gate.
          employeeOnly: true,
        },
      ],
      [
        onCoverPhotoSelection,
        onRotate,
        onDelete,
        onViewFaceConfidence,
        onViewTaggedCampers,
      ],
    )

    // Honors an album deep link (`/album/:id?photo=`) from the FR Diagnostics
    // page: scroll to the linked photo once the grid has something to scroll
    // to. Hooks must run unconditionally, so this sits above the isVisible
    // early-return and gates on readiness instead.
    //
    // Passes the photo COUNT, not a boolean: the linked photo is usually one of
    // the newest, so in a paginated album it isn't on page 1 and the anchor
    // appears only as later pages arrive. A boolean latches on the first page
    // and never retries. 0 means "not ready".
    useScrollToPhotoParam(isVisible ? photos.length : 0)

    if (!isVisible) return null

    return (
      <div data-testid='photo-gallery'>
        <PhotoGallery
          photos={photos}
          galleryPhotos={transformedThumbnailPhotos}
          fallbackPhoto={fallbackImage}
          actions={actions.filter(
            (action) =>
              getIsReleasedToUser(action.feature) &&
              // `employeeOnly` actions require a CampMinder-employee email
              // (@campminder.com), but the server-side backstop differs per
              // action. For "See Facial Recognition Confidence" this is a
              // best-effort UX gate only — the faces backend independently
              // enforces the authoritative `campminderstaff` gate (403 to a
              // non-staff caller), so no data is exposed if the affordance
              // shows for a non-staff user. "Tagged Campers" has NO such
              // backstop: `/photos/person-tags/list` grants
              // `clientemployee`/`system`, not `campminderstaff`, so this is a
              // UI-visibility gate only — any camp-admin token can already
              // call that endpoint directly today (accepted deliberately;
              // see AB#23610 plan D2). See `useIsCampMinderEmployee` for the
              // employee-check rationale.
              (!action.employeeOnly || isCampMinderEmployee),
          )}
        />
        {isFaceConfidenceOpen && selectedFacePhoto && (
          <FaceConfidenceModal
            key={selectedFacePhoto.photoId}
            isOpen={isFaceConfidenceOpen}
            onClose={onFaceConfidenceModalClose}
            photoId={selectedFacePhoto.photoId}
            clientId={selectedFacePhoto.clientId}
          />
        )}
        {isTaggedCampersOpen && selectedTagPhoto && (
          <PhotoTagModal
            key={selectedTagPhoto.photoId}
            isOpen={isTaggedCampersOpen}
            onClose={onTaggedCampersModalClose}
            photoId={selectedTagPhoto.photoId}
            clientId={selectedTagPhoto.clientId}
            photoUrl={selectedTagPhoto.photoUrl}
          />
        )}
      </div>
    )
  })

DefaultGallery.displayName = 'DefaultGallery'
